import { ReactNode, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import IconEdit from '@/icons/Edit.svg';
import { AddressRaw, isAddressEmpty } from '@/models/address';
import { Store } from '@/models/store';

import { getStoreError, StoreForm, useStoreState } from './StoreForm';

type UpdateStoreProps = {
  store: Store;
  updateStore: (
    data: {
      storeId: string;
    } & Endpoints['PATCH /stores/:id']['body']
  ) => Promise<void>;
  isPending: boolean;
  size?: 'medium' | 'large';
  variant?: 'default' | 'brand';
  allowSalesAdvisorsEdition?: boolean;
  trigger?: ReactNode;
};

export const UpdateStore = ({ store, variant, trigger, ...props }: UpdateStoreProps) => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const label = t({ id: 'settings.stores.edit-modal.trigger', message: `Edit ${store.name}` });

  return (
    <Dialog
      isOpen={open}
      onOpenChange={setOpen}
      trigger={
        trigger ?? (
          <Button
            variant={variant === 'brand' ? 'secondary-brand' : 'secondary'}
            iconOnly
            size={variant === 'brand' ? 'medium' : 'small'}
            ariaLabel={label}
            tooltip={label}
          >
            <IconEdit />
          </Button>
        )
      }
      title={<Trans id="settings.stores.edit-modal.title">Edit {store.name}</Trans>}
    >
      <UpdateStoreContent store={store} variant={variant} setOpen={setOpen} {...props} />
    </Dialog>
  );
};

const UpdateStoreContent = ({
  store,
  updateStore,
  isPending,
  size,
  variant,
  setOpen,
  allowSalesAdvisorsEdition,
}: UpdateStoreProps & { setOpen: (isOpen: boolean) => void }) => {
  const state = useStoreState(store);

  return (
    <>
      <main style={{ overflow: 'auto', maxHeight: '70vh' }}>
        <StoreForm
          state={state}
          size={size}
          variant={variant}
          allowSalesAdvisorsEdition={allowSalesAdvisorsEdition}
        />
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant={variant === 'brand' ? 'brand' : 'primary'}
            size={size}
            type="submit"
            isLoading={isPending}
            onPress={() => {
              if (getStoreError({ state, allowSalesAdvisorsEdition }).hasError) {
                state.setShowErrors(true);

                return;
              }

              updateStore({
                ...state,
                storeId: store.id,
                groups: state.groups?.map((group) => ({ id: group.id })),
                address: state.address as AddressRaw,
                logisticsAddress: isAddressEmpty(state.logisticsAddress)
                  ? null
                  : (state.logisticsAddress as AddressRaw),
                salesAdvisors: allowSalesAdvisorsEdition ? state.salesAdvisors : undefined,
              })
                .then(() => {
                  state.setShowErrors(false);
                  setOpen(false);
                })
                .catch((err) => {
                  console.error(err);
                });
            }}
          >
            <Trans id="settings.stores.edit-modal.submit">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
